import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Megaphone,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Pause,
  Play,
  Euro,
  X,
  Bot,
  FileBarChart,
  LayoutList,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { extractApiErrorMessage } from "@/lib/api-errors";
import { useGlobalToast } from "@/contexts/ToastContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import AdsAssistant, { type AssistantPlatformConfig } from "@/components/AdsAssistant";
import AdsReports, { type ReportsPlatformConfig } from "@/components/AdsReports";
import MetaConversionsCard from "@/components/MetaConversionsCard";

/**
 * SUPERADMIN → Marketing → Meta Ads (Facebook e Instagram), sep-2026.
 * Mismo modelo que Google Ads: lectura real, pausar/activar y presupuesto con
 * confirmación, y un asistente IA que propone (Luis aplica con un clic).
 */

interface MetaStatus {
  configured: boolean;
  code?: string;
  adAccountId?: string;
  accountName?: string;
  accountStatus?: number;
  currency?: string;
  timeZone?: string;
  pageName?: string | null;
  pixelId?: string | null;
  pixelName?: string | null;
  pixelLastFiredTime?: string | null;
  instagramUserId?: string | null;
  apiVersion?: string;
  maxDailyBudgetEur?: number;
}

interface MetaCampaign {
  id: string;
  name: string;
  objective: string;
  status: string;
  effectiveStatus: string;
  dailyBudgetEur: number | null;
  lifetimeBudgetEur: number | null;
  spendEur: number;
  impressions: number;
  reach: number;
  frequency: number;
  linkClicks: number;
  ctrPct: number;
  cpcEur: number;
  registrations: number;
  landingPageViews: number;
}

type Tab = "campaigns" | "assistant" | "reports";

const TABS: Array<{ id: Tab; label: string; icon: typeof Bot }> = [
  { id: "campaigns", label: "Campañas", icon: LayoutList },
  { id: "assistant", label: "Asistente IA", icon: Bot },
  { id: "reports", label: "Informes", icon: FileBarChart },
];

const RANGES = [7, 14, 30, 90] as const;

const OBJECTIVES: Record<string, string> = {
  OUTCOME_LEADS: "Clientes potenciales",
  OUTCOME_TRAFFIC: "Tráfico",
  OUTCOME_AWARENESS: "Reconocimiento",
  OUTCOME_ENGAGEMENT: "Interacción",
  OUTCOME_SALES: "Ventas",
};

const META_ASSISTANT: AssistantPlatformConfig = {
  apiBase: "/admin/meta-ads/ai",
  title: "Asistente de Meta Ads",
  platformLabel: "Meta Ads",
  storageKey: "rm_meta_ads_ai_chat",
  mediaBase: "/admin/meta-ads/media",
  suggestions: [
    "¿Cómo van las campañas de Meta en los últimos 14 días y cuánto nos cuesta cada registro real?",
    "Revisa la segmentación y dime si estamos llegando a dueños de negocio o a consumidores.",
    "Prepara una campaña de registros para dueños de restaurantes en España con 10 €/día (todo en pausa).",
    "Genera 2 imágenes para un anuncio de agenda online para peluquerías y propón el anuncio.",
  ],
};

const META_REPORTS: ReportsPlatformConfig = {
  apiBase: "/admin/meta-ads/ai",
  description:
    "Analiza campañas, audiencias, creatividades, ubicaciones, frecuencia y registros reales de ReservaManager, y lo compara con el informe anterior. Tarda 1-3 minutos; puedes seguir usando el panel.",
};

function formatEur(value: number): string {
  return `${value.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    ACTIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
    PAUSED: "bg-amber-50 text-amber-700 border-amber-200",
    WITH_ISSUES: "bg-red-50 text-red-700 border-red-200",
    IN_PROCESS: "bg-sky-50 text-sky-700 border-sky-200",
  };
  const labels: Record<string, string> = {
    ACTIVE: "Activa",
    PAUSED: "Pausada",
    WITH_ISSUES: "Con problemas",
    IN_PROCESS: "En proceso",
    CAMPAIGN_PAUSED: "Pausada",
    PENDING_REVIEW: "En revisión",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${styles[status] ?? "bg-gray-50 text-gray-600 border-gray-200"}`}>
      {labels[status] ?? status}
    </span>
  );
}

export default function MetaAds() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [status, setStatus] = useState<MetaStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);
  const [campaigns, setCampaigns] = useState<MetaCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [budgetTarget, setBudgetTarget] = useState<MetaCampaign | null>(null);
  const [budgetValue, setBudgetValue] = useState("");
  const [budgetSaving, setBudgetSaving] = useState(false);
  const [tab, setTab] = useState<Tab>("campaigns");
  const [assistantDraft, setAssistantDraft] = useState<string | null>(null);
  const clearAssistantDraft = useCallback(() => setAssistantDraft(null), []);
  const askAssistant = useCallback((text: string) => {
    setAssistantDraft(text);
    setTab("assistant");
  }, []);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const { data } = await apiClient.get<{ success: true; data: MetaStatus }>("/admin/meta-ads/status");
      setStatus(data.data);
    } catch (err) {
      setStatusError(extractApiErrorMessage(err, "No se pudo comprobar la conexión con Meta"));
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const { data } = await apiClient.get<{ success: true; data: MetaCampaign[] }>("/admin/meta-ads/campaigns", { params: { days } });
      setCampaigns(data.data);
    } catch (err) {
      setCampaignsError(extractApiErrorMessage(err, "No se pudieron cargar las campañas"));
    } finally {
      setCampaignsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (status?.configured) void loadCampaigns();
  }, [status?.configured, loadCampaigns]);

  const totals = useMemo(() => {
    const spend = campaigns.reduce((acc, c) => acc + c.spendEur, 0);
    const reach = campaigns.reduce((acc, c) => acc + c.reach, 0);
    const linkClicks = campaigns.reduce((acc, c) => acc + c.linkClicks, 0);
    const impressions = campaigns.reduce((acc, c) => acc + c.impressions, 0);
    const registrations = campaigns.reduce((acc, c) => acc + c.registrations, 0);
    return {
      spend,
      reach,
      linkClicks,
      registrations,
      ctr: impressions > 0 ? (linkClicks / impressions) * 100 : 0,
      cpc: linkClicks > 0 ? spend / linkClicks : 0,
      cpr: registrations > 0 ? spend / registrations : 0,
    };
  }, [campaigns]);

  const handleToggle = useCallback(
    async (campaign: MetaCampaign) => {
      const enabling = campaign.status !== "ACTIVE";
      const ok = await confirm({
        title: enabling ? "¿Activar la campaña?" : "¿Pausar la campaña?",
        message: enabling
          ? `"${campaign.name}" empezará a emitir anuncios y a GASTAR según sus presupuestos.`
          : `"${campaign.name}" dejará de emitir anuncios.`,
        confirmText: enabling ? "Sí, activar" : "Sí, pausar",
        danger: enabling,
      });
      if (!ok) return;
      setActionId(campaign.id);
      try {
        const { data } = await apiClient.patch<{ success: true; message: string }>(`/admin/meta-ads/objects/${campaign.id}/status`, {
          level: "campaign",
          status: enabling ? "ACTIVE" : "PAUSED",
        });
        showToast(data.message, "success");
        await loadCampaigns();
      } catch (err) {
        showToast(extractApiErrorMessage(err, "No se pudo cambiar el estado"), "error");
      } finally {
        setActionId(null);
      }
    },
    [confirm, showToast, loadCampaigns],
  );

  const handleSaveBudget = useCallback(async () => {
    if (!budgetTarget) return;
    const value = Number(budgetValue.replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) {
      showToast("Introduce un presupuesto válido en euros/día", "error");
      return;
    }
    const ok = await confirm({
      title: "Confirmar cambio de presupuesto",
      message: `"${budgetTarget.name}": de ${formatEur(budgetTarget.dailyBudgetEur ?? 0)}/día a ${formatEur(value)}/día.`,
      confirmText: "Cambiar presupuesto",
      danger: value > (budgetTarget.dailyBudgetEur ?? 0),
    });
    if (!ok) return;
    setBudgetSaving(true);
    try {
      await apiClient.patch(`/admin/meta-ads/objects/${budgetTarget.id}/budget`, { level: "campaign", dailyBudgetEur: value });
      showToast("Presupuesto actualizado", "success");
      setBudgetTarget(null);
      await loadCampaigns();
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo cambiar el presupuesto"), "error");
    } finally {
      setBudgetSaving(false);
    }
  }, [budgetTarget, budgetValue, confirm, showToast, loadCampaigns]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary-600" /> Meta Ads
          </h1>
          <p className="text-sm text-gray-500 mt-1">Facebook e Instagram — integración directa con la Marketing API.</p>
        </div>
        <button
          onClick={() => {
            void loadStatus();
            if (status?.configured) void loadCampaigns();
          }}
          className="inline-flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" /> Actualizar
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5">
        {statusLoading ? (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Comprobando conexión...
          </p>
        ) : statusError ? (
          <p className="flex items-start gap-2 text-sm text-red-600 break-words">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {statusError}
          </p>
        ) : status && !status.configured ? (
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">Meta Ads no está conectado</p>
              <p className="text-sm text-gray-500 mt-1">
                Falta la variable <code className="bg-gray-100 px-1 rounded">META_ADS_ACCESS_TOKEN</code> en Railway: token de un
                usuario del sistema del Business Manager con permisos de anuncios y la cuenta publicitaria, la página y el Pixel
                asignados.
              </p>
            </div>
          </div>
        ) : status ? (
          <div className="flex items-center gap-x-6 gap-y-2 flex-wrap text-sm">
            <span className="inline-flex items-center gap-2 font-medium text-emerald-700">
              <CheckCircle className="w-5 h-5" /> Conectado
            </span>
            <span className="text-gray-600">
              <strong>{status.accountName}</strong> · {status.adAccountId}
            </span>
            <span className="text-gray-500">Página: {status.pageName ?? "sin asignar"}</span>
            <span className={status.pixelId ? "text-gray-500" : "text-amber-700"}>
              Pixel: {status.pixelName ?? status.pixelId ?? "sin asignar"}
              {status.pixelLastFiredTime ? ` (último evento ${new Date(status.pixelLastFiredTime).toLocaleString("es-ES")})` : ""}
            </span>
            <span className="text-gray-500">Instagram: {status.instagramUserId ? "conectado" : "no conectado"}</span>
            <span className="text-gray-500">
              {status.currency} · {status.timeZone} · API {status.apiVersion}
            </span>
          </div>
        ) : null}
      </div>

      {status?.configured && (
        <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === id ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      )}

      {status?.configured && (
        <div className={tab === "assistant" ? "" : "hidden"}>
          <AdsAssistant config={META_ASSISTANT} draft={assistantDraft} onDraftConsumed={clearAssistantDraft} onApplied={() => void loadCampaigns()} />
        </div>
      )}

      {status?.configured && (
        <div className={tab === "reports" ? "" : "hidden"}>
          <AdsReports config={META_REPORTS} onAskAssistant={askAssistant} />
        </div>
      )}

      {status?.configured && tab === "campaigns" && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-semibold text-gray-900">Rendimiento</h2>
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {RANGES.map((r) => (
                <button
                  key={r}
                  onClick={() => setDays(r)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    days === r ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {r} días
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: "Gasto", value: formatEur(totals.spend) },
              { label: "Alcance", value: totals.reach.toLocaleString("es-ES") },
              { label: "Clics en enlace", value: totals.linkClicks.toLocaleString("es-ES") },
              { label: "CTR enlace", value: `${totals.ctr.toFixed(2)} %` },
              { label: "CPC", value: totals.linkClicks > 0 ? formatEur(totals.cpc) : "—" },
              { label: "Registros (Pixel)", value: totals.registrations.toLocaleString("es-ES") },
              { label: "Coste/registro", value: totals.registrations > 0 ? formatEur(totals.cpr) : "—" },
            ].map((kpi) => (
              <div key={kpi.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500">{kpi.label}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {campaignsLoading ? (
              <p className="flex items-center gap-2 text-sm text-gray-500 p-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando campañas...
              </p>
            ) : campaignsError ? (
              <p className="flex items-center gap-2 text-sm text-red-600 p-6">
                <AlertTriangle className="w-4 h-4" /> {campaignsError}
              </p>
            ) : campaigns.length === 0 ? (
              <p className="text-sm text-gray-500 p-6 text-center">
                No hay campañas todavía. Pide al asistente que prepare la primera (se crea en pausa).
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Campaña</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Presupuesto/día</th>
                      <th className="px-4 py-3 text-right">Gasto</th>
                      <th className="px-4 py-3 text-right">Clics enlace</th>
                      <th className="px-4 py-3 text-right">Frecuencia</th>
                      <th className="px-4 py-3 text-right">Registros</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {campaigns.map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{campaign.name}</p>
                          <p className="text-xs text-gray-400">{OBJECTIVES[campaign.objective] ?? campaign.objective}</p>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={campaign.effectiveStatus || campaign.status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          {campaign.dailyBudgetEur !== null ? formatEur(campaign.dailyBudgetEur) : <span className="text-gray-400">En conjuntos</span>}
                        </td>
                        <td className="px-4 py-3 text-right">{formatEur(campaign.spendEur)}</td>
                        <td className="px-4 py-3 text-right">{campaign.linkClicks.toLocaleString("es-ES")}</td>
                        <td className="px-4 py-3 text-right">{campaign.frequency.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right">{campaign.registrations}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => void handleToggle(campaign)}
                              disabled={actionId === campaign.id}
                              title={campaign.status === "ACTIVE" ? "Pausar" : "Activar"}
                              className={`p-1.5 rounded-lg border transition-colors disabled:opacity-50 ${
                                campaign.status === "ACTIVE"
                                  ? "border-amber-200 text-amber-600 hover:bg-amber-50"
                                  : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"
                              }`}
                            >
                              {actionId === campaign.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : campaign.status === "ACTIVE" ? (
                                <Pause className="w-4 h-4" />
                              ) : (
                                <Play className="w-4 h-4" />
                              )}
                            </button>
                            {campaign.dailyBudgetEur !== null && (
                              <button
                                onClick={() => {
                                  setBudgetTarget(campaign);
                                  setBudgetValue(String(campaign.dailyBudgetEur));
                                }}
                                title="Modificar presupuesto"
                                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50"
                              >
                                <Euro className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <MetaConversionsCard />
        </>
      )}

      {budgetTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Modificar presupuesto</h3>
              <button onClick={() => setBudgetTarget(null)} className="p-1 rounded hover:bg-gray-100 text-gray-400" title="Cerrar">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-1">{budgetTarget.name}</p>
            <p className="text-sm text-gray-500 mb-4">
              Presupuesto actual: <strong>{formatEur(budgetTarget.dailyBudgetEur ?? 0)}/día</strong>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nuevo presupuesto (€/día)</label>
            <input
              type="number"
              min="1"
              step="0.5"
              value={budgetValue}
              onChange={(e) => setBudgetValue(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">
              El servidor aplica el tope de seguridad ({status?.maxDailyBudgetEur ?? 100} €/día).
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setBudgetTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
                Cancelar
              </button>
              <button
                onClick={() => void handleSaveBudget()}
                disabled={budgetSaving}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {budgetSaving ? "Guardando..." : "Continuar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
